// VOY-417 — shared correction flow.
//
// Three entry points wire through the SAME VOY-458 backend:
//   §3 Travel style & preferences — per-attribute "Correct"
//   §7 Memory feed — per-entry "Forget"
//   §8 Memory Editor — facts list with Confirm / Correct / Pin / Delete
//
// This hook centralises the four endpoints + state so all three surfaces
// share one flow. Optimistic UI + parent-supplied refresh callback.
//
// VOY-458 endpoints (see VOY-458 Linear comment for full contract):
//   POST /api/v2/profile/:userId/memory/confirm
//   POST /api/v2/profile/:userId/memory/correct
//   POST /api/v2/profile/:userId/memory/pin
//   POST /api/v2/profile/:userId/memory/delete
//
// All four are dimension-keyed against PROFILE_DIMENSIONS. Style-attribute
// keys (airline_class, hotel_tier, etc.) are NOT currently in that list —
// callers that operate on style_attributes use STYLE_ATTRIBUTE_TO_DIMENSION
// below to map known attributes to their nearest PROFILE_DIMENSION; the
// rest disable the affordance (see TravelStyleSection's `mappedDimension`).

import { useState, useCallback } from 'react'
import {
  confirmMemoryFact,
  correctMemoryFact,
  pinMemoryFact,
  deleteMemoryFact,
} from '@/lib/profile'
import type { MemoryAction, ProfileDimension, StyleAttributeKey } from '@/types/profile'

/**
 * Stale-spec note (VOY-417): VOY-503's 8 style_attribute keys do not all
 * have a 1:1 entry in VOY-438's PROFILE_DIMENSIONS list. We map the ones
 * that have a natural counterpart; the rest are `null` and the UI
 * disables Correct on them with a "Backend coverage pending" wire-point.
 *
 * Backend follow-up: extending PROFILE_DIMENSIONS to include
 * `airline_class`, `seat`, `climate`, `regions`, `checked_bag` would let
 * the user correct every style_attribute directly.
 */
export const STYLE_ATTRIBUTE_TO_DIMENSION: Partial<Record<StyleAttributeKey, ProfileDimension>> = {
  hotel_tier: 'accommodation',
  // `trip_length` maps loosely to the quiz dim `fast_vs_slow` — same axis
  // (short hops vs deep stays). The override flow writes a categorical
  // value the resolver respects.
  trip_length: 'fast_vs_slow',
  // `booking_window` (how far ahead the user books) maps to the quiz
  // dim `planned_vs_spontaneous`.
  booking_window: 'planned_vs_spontaneous',
  // airline_class / seat / climate / regions / checked_bag — no direct
  // PROFILE_DIMENSIONS counterpart yet. Wire-point only.
}

export interface FactCorrectionState {
  /** True when a request is in flight. UI disables actions during this. */
  saving: boolean
  /** Last error message, if any. UI surfaces this in the modal/inline. */
  error: string | null
  /** Which action is currently being applied (for inline spinners). */
  pendingAction: MemoryAction | null
  /** Which dimension is being acted on (for per-row spinners). */
  pendingDimension: string | null
}

export interface FactCorrectionApi {
  state: FactCorrectionState
  /** POST /memory/confirm. `value` is optional when a prior override exists. */
  confirm: (dimension: string, value?: unknown) => Promise<boolean>
  /**
   * POST /memory/correct. `newValue` may be null. `reason` / `priorValue`
   * forward to the backend's tombstone write.
   */
  correct: (
    dimension: string,
    newValue: unknown,
    opts?: { reason?: string; priorValue?: unknown },
  ) => Promise<boolean>
  /** POST /memory/pin. `value` required when no prior override exists. */
  pin: (dimension: string, value?: unknown) => Promise<boolean>
  /**
   * POST /memory/delete — tombstones first, then removes the override
   * row. The deleted AI claim becomes a negative signal the agent will
   * suppress on its next pass.
   */
  remove: (
    dimension: string,
    opts?: { currentValue?: unknown; reason?: string },
  ) => Promise<boolean>
  /** Clear the error message manually (e.g. on modal close). */
  resetError: () => void
}

export interface UseFactCorrectionOptions {
  userId: string
  /**
   * Fired after every successful action. Use to refetch agent profile /
   * agent context so the page re-renders with the new resolved values
   * + updated confirmed_count / pinned state.
   */
  onSuccess?: (action: MemoryAction, dimension: string) => void
}

/**
 * Shared correction flow. Used by Memory Editor (§8), per-attribute
 * Correct (§3, where mapped), and memory feed Forget (§7, where the
 * backend gains an untyped-observation endpoint — currently disabled).
 */
export function useFactCorrection({ userId, onSuccess }: UseFactCorrectionOptions): FactCorrectionApi {
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pendingAction, setPendingAction] = useState<MemoryAction | null>(null)
  const [pendingDimension, setPendingDimension] = useState<string | null>(null)

  const begin = useCallback((action: MemoryAction, dimension: string) => {
    setSaving(true)
    setError(null)
    setPendingAction(action)
    setPendingDimension(dimension)
  }, [])

  const end = useCallback((ok: boolean, action: MemoryAction, dimension: string, errMsg?: string) => {
    setSaving(false)
    setPendingAction(null)
    setPendingDimension(null)
    if (!ok && errMsg) setError(errMsg)
    if (ok) onSuccess?.(action, dimension)
  }, [onSuccess])

  const confirm = useCallback(async (dimension: string, value?: unknown): Promise<boolean> => {
    begin('confirm', dimension)
    try {
      await confirmMemoryFact(userId, dimension, value)
      end(true, 'confirm', dimension)
      return true
    } catch (err) {
      end(false, 'confirm', dimension, err instanceof Error ? err.message : 'Confirm failed')
      return false
    }
  }, [userId, begin, end])

  const correct = useCallback(async (
    dimension: string,
    newValue: unknown,
    opts?: { reason?: string; priorValue?: unknown },
  ): Promise<boolean> => {
    begin('correct', dimension)
    try {
      await correctMemoryFact(userId, dimension, newValue, opts)
      end(true, 'correct', dimension)
      return true
    } catch (err) {
      end(false, 'correct', dimension, err instanceof Error ? err.message : 'Correction failed')
      return false
    }
  }, [userId, begin, end])

  const pin = useCallback(async (dimension: string, value?: unknown): Promise<boolean> => {
    begin('pin', dimension)
    try {
      await pinMemoryFact(userId, dimension, value)
      end(true, 'pin', dimension)
      return true
    } catch (err) {
      end(false, 'pin', dimension, err instanceof Error ? err.message : 'Pin failed')
      return false
    }
  }, [userId, begin, end])

  const remove = useCallback(async (
    dimension: string,
    opts?: { currentValue?: unknown; reason?: string },
  ): Promise<boolean> => {
    begin('delete', dimension)
    try {
      await deleteMemoryFact(userId, dimension, opts)
      end(true, 'delete', dimension)
      return true
    } catch (err) {
      end(false, 'delete', dimension, err instanceof Error ? err.message : 'Delete failed')
      return false
    }
  }, [userId, begin, end])

  const resetError = useCallback(() => setError(null), [])

  return {
    state: { saving, error, pendingAction, pendingDimension },
    confirm,
    correct,
    pin,
    remove,
    resetError,
  }
}
